import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import LoadingScreen from '../components/LoadingScreen';
import {
  RefreshCw, AlertTriangle, History, Search, Facebook, User, Database, Zap, Download,
  ChevronLeft, ChevronRight, Wallet, Power, PlusCircle, Trash2, ArrowUp, ArrowDown,
} from 'lucide-react';

const API = 'https://api.glowytics.com/api';

// Archive can look arbitrarily far back; live is capped by the platform API retention (~30d).
const ARCHIVE_DAY_OPTIONS = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last 1 year', value: 365 },
];
// Google change_event API only retains ~30 days, so Google is capped there.
// Meta activities go back further, so 90 days is Meta-only.
const LIVE_DAY_OPTIONS = [
  { label: 'Today', value: 0 },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 29 },
  { label: 'Last 90 days (Meta)', value: 90 },
];

const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const EN_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Google change-event actions arrive Turkish from the backend script; translate at
// display time so both live and archived (historically stored) rows read English.
const ACTION_LABELS = { 'Oluşturuldu': 'Created', 'Güncellendi': 'Updated', 'Silindi': 'Removed' };
const actionLabel = (a) => ACTION_LABELS[a] || a || '—';

// Heuristic colors for action types (TR + EN keywords)
const actionTone = (action = '') => {
  const a = action.toLowerCase();
  if (a.includes('budget') || a.includes('bütçe') || a.includes('amount')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (a.includes('status') || a.includes('durum') || a.includes('paused') || a.includes('active')) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (a.includes('oluştur') || a.includes('create')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (a.includes('sil') || a.includes('remove') || a.includes('delete')) return 'bg-rose-50 text-rose-700 border-rose-200';
  if (a.includes('bill') || a.includes('fatura')) return 'bg-violet-50 text-violet-700 border-violet-200';
  return 'bg-gray-50 text-gray-600 border-gray-200';
};

// --- Change classification: separates meaningful edits from delivery/system noise ---
const classify = (c) => {
  const t = `${c.action || ''} ${c.field || ''}`.toLowerCase();
  if (/deliver|delivery|recommendation|in review|under review|reviewed|learning|ad review/.test(t)) return 'noise';
  if (/budget|bütçe|amount|bid|teklif|cost ?cap|roas|spend limit/.test(t)) return 'budget';
  if (/\b(create|created|oluştur|added|new)\b/.test(t)) return 'create';
  if (/remove|delete|sil|archiv|arşiv/.test(t)) return 'remove';
  if (/status|durum|pause|paused|duraklat|reactivat|resume|enable|disable|activ|aktif|inactive/.test(t)) return 'status';
  return 'other';
};

const statusDir = (c) => {
  const t = `${c.action || ''} ${c.new_value || ''}`.toLowerCase();
  if (/paus|durdu|duraklat|disable|inactive|stopped|off\b/.test(t)) return 'paused';
  if (/reactivat|resume|enable|\bactiv|aktif|running|\bon\b/.test(t)) return 'activated';
  return null;
};

// Best-effort numeric parse tolerant of tr ("1.234,56") and en ("1,234.56") formats + currency.
const parseAmt = (s) => {
  if (s == null) return null;
  let t = String(s).replace(/[^\d.,-]/g, '');
  if (!t) return null;
  const lc = t.lastIndexOf(','), ld = t.lastIndexOf('.');
  if (lc > -1 && ld > -1) {
    if (lc > ld) t = t.replace(/\./g, '').replace(',', '.'); // tr
    else t = t.replace(/,/g, '');                            // en
  } else if (lc > -1) {
    t = (t.length - lc - 1) === 3 ? t.replace(/,/g, '') : t.replace(',', '.');
  }
  const n = parseFloat(t);
  return isNaN(n) ? null : n;
};

const fmtAmt = (n) => (n == null ? '—' : Math.round(n).toLocaleString('en-US') + '₺');
const fmtPct = (p) => (p == null ? '' : `${p > 0 ? '+' : ''}${p.toFixed(0)}%`);

// Bucket a change datetime into an Istanbul YYYY-MM-DD day.
const istDay = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
};

const PlatformBadge = ({ platform }) => {
  const isGoogle = platform === 'GOOGLE_ADS';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${isGoogle ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
      {isGoogle ? <Search className="w-3 h-3" /> : <Facebook className="w-3 h-3" />}
      {isGoogle ? 'Google' : 'Meta'}
    </span>
  );
};

const fmtDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
};

const StatCard = ({ title, children }) => (
  <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-4">
    <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 mb-2">{title}</p>
    {children}
  </div>
);

const sortedEntries = (obj = {}) => Object.entries(obj).sort((a, b) => b[1] - a[1]);

// --- Month calendar: marks days with Google/Meta changes; click a day to filter ---
const MonthCalendar = ({ dayMap, selectedDay, onSelectDay, initialDay }) => {
  const init = initialDay ? new Date(initialDay + 'T12:00:00') : new Date();
  const [view, setView] = useState({ y: init.getFullYear(), m: init.getMonth() });
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });

  const { y, m } = view;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const lead = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-first offset
  const cells = [...Array(lead).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const pad = (n) => String(n).padStart(2, '0');
  const maxDay = Math.max(1, ...Object.entries(dayMap)
    .filter(([k]) => k.startsWith(`${y}-${pad(m + 1)}`)).map(([, v]) => v.total));
  const monthTotal = Object.entries(dayMap)
    .filter(([k]) => k.startsWith(`${y}-${pad(m + 1)}`))
    .reduce((s, [, v]) => s + v.total, 0);

  const shift = (delta) => setView((v) => {
    const d = new Date(v.y, v.m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  return (
    <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeft className="w-4 h-4 text-gray-500" /></button>
          <span className="font-semibold text-gray-800 text-sm w-32 text-center">{EN_MONTHS[m]} {y}</span>
          <button onClick={() => shift(1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronRight className="w-4 h-4 text-gray-500" /></button>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500">
          <span className="text-gray-400">{monthTotal} changes</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Google</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-400" /> Meta</span>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {EN_WEEK.map((d) => <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d == null) return <div key={`e${i}`} />;
          const key = `${y}-${pad(m + 1)}-${pad(d)}`;
          const e = dayMap[key];
          const isToday = key === today;
          const isSel = key === selectedDay;
          return (
            <button
              key={key}
              onClick={() => e && onSelectDay(isSel ? null : key)}
              disabled={!e}
              className={`relative h-14 rounded-lg border p-1 text-left transition
                ${isSel ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/40'
                  : e ? 'border-gray-200 hover:border-blue-300 cursor-pointer bg-white'
                    : 'border-gray-100 bg-gray-50/50 cursor-default'}`}
            >
              <span className={`text-[11px] ${isToday ? 'font-bold text-blue-600' : 'text-gray-400'}`}>{d}</span>
              {e && (
                <>
                  <span className="absolute top-1 right-1 text-[10px] font-bold text-gray-700">{e.total}</span>
                  <div className="absolute bottom-1 left-1 right-1 flex gap-0.5 items-end h-4">
                    {e.G > 0 && <div className="bg-blue-500 rounded-sm flex-1" style={{ height: `${Math.max(20, (e.G / maxDay) * 100)}%` }} title={`Google ${e.G}`} />}
                    {e.M > 0 && <div className="bg-indigo-400 rounded-sm flex-1" style={{ height: `${Math.max(20, (e.M / maxDay) * 100)}%` }} title={`Meta ${e.M}`} />}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function AdsChangeHistory() {
  const { selectedBrand } = useAuth();
  const [source, setSource] = useState('archive'); // 'archive' (DB, sınırsız geçmiş) | 'live' (API, ~30g)
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformFilter, setPlatformFilter] = useState('ALL');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [entityFilter, setEntityFilter] = useState('ALL');
  const [actorFilter, setActorFilter] = useState('ALL');
  const [budgetOnly, setBudgetOnly] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [search, setSearch] = useState('');

  const dayOptions = source === 'archive' ? ARCHIVE_DAY_OPTIONS : LIVE_DAY_OPTIONS;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = source === 'archive'
        ? `${API}/ads-change-history/archive?days=${days}`
        : `${API}/ads-change-history/live?days=${Math.min(days, 90)}`;
      const res = await fetch(endpoint, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand?.id || localStorage.getItem('currentBrandId') || '' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setData(json);
      setSelectedDay(null);
      if (json.errors?.google) toast.error('Google: ' + json.errors.google.slice(0, 120));
      if (json.errors?.meta) toast.error('Meta: ' + json.errors.meta.slice(0, 120));
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [source, days, selectedBrand]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const switchSource = (s) => {
    setSource(s);
    setDays(s === 'archive' ? 30 : 14);
  };

  // Calendar day map — from ALL changes (independent of facet filters), per Istanbul day.
  const dayMap = useMemo(() => {
    const m = {};
    for (const c of (data?.changes || [])) {
      const day = istDay(c.datetime);
      if (!day) continue;
      const e = m[day] || (m[day] = { G: 0, M: 0, total: 0 });
      if (c.platform === 'GOOGLE_ADS') e.G++; else if (c.platform === 'META_ADS') e.M++;
      e.total++;
    }
    return m;
  }, [data]);
  const latestDay = useMemo(() => {
    const keys = Object.keys(dayMap).sort();
    return keys.length ? keys[keys.length - 1] : null;
  }, [dayMap]);

  const rows = useMemo(() => {
    let r = data?.changes || [];
    if (selectedDay) r = r.filter(c => istDay(c.datetime) === selectedDay);
    if (platformFilter !== 'ALL') r = r.filter(c => c.platform === platformFilter);
    if (actionFilter !== 'ALL') r = r.filter(c => (c.action || '—') === actionFilter);
    if (entityFilter !== 'ALL') r = r.filter(c => (c.entity_type || '—') === entityFilter);
    if (actorFilter !== 'ALL') r = r.filter(c => (c.actor || '—') === actorFilter);
    if (budgetOnly) r = r.filter(c => classify(c) === 'budget');
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(c =>
        [c.entity_name, c.entity_type, c.action, c.actor, c.field, c.old_value, c.new_value]
          .filter(Boolean).some(v => String(v).toLowerCase().includes(q))
      );
    }
    return r;
  }, [data, selectedDay, platformFilter, actionFilter, entityFilter, actorFilter, budgetOnly, search]);

  // Digest — condenses the filtered rows into human-readable highlights.
  const digest = useMemo(() => {
    const budget = { count: 0, up: 0, down: 0, items: [] };
    const status = { paused: 0, activated: 0 };
    let created = 0, removed = 0, noise = 0, other = 0;
    for (const c of rows) {
      const cat = classify(c);
      if (cat === 'noise') { noise++; continue; }
      if (cat === 'budget') {
        const o = parseAmt(c.old_value), n = parseAmt(c.new_value);
        if (o != null && n != null && o !== n) {
          const delta = n - o;
          const pct = o !== 0 ? (delta / Math.abs(o)) * 100 : null;
          if (delta > 0) budget.up++; else budget.down++;
          budget.count++;
          budget.items.push({
            key: c.entity_id || `${c.platform}:${c.entity_name || c.entity_type || '?'}`,
            when: String(c.datetime || ''),
            name: c.entity_name || c.entity_type || '—', platform: c.platform, from: o, to: n, delta, pct,
          });
        }
        continue;
      }
      if (cat === 'status') { const d = statusDir(c); if (d === 'paused') status.paused++; else if (d === 'activated') status.activated++; continue; }
      if (cat === 'create') { created++; continue; }
      if (cat === 'remove') { removed++; continue; }
      other++;
    }
    // Net toplam bütçe etkisi: her öğe için dönemdeki ilk eski → son yeni bütçeyi
    // alıp topla. Aynı öğe birden çok kez değiştiyse net sonuç doğru çıkar.
    const totalsOf = (items) => {
      const byEntity = new Map();
      for (const it of items) {
        const g = byEntity.get(it.key);
        if (!g) { byEntity.set(it.key, { first: it, last: it }); continue; }
        if (it.when < g.first.when) g.first = it;
        if (it.when >= g.last.when) g.last = it;
      }
      let before = 0, after = 0, up = 0, down = 0;
      for (const g of byEntity.values()) {
        before += g.first.from; after += g.last.to;
        const d = g.last.to - g.first.from;
        if (d > 0) up++; else if (d < 0) down++;
      }
      const delta = after - before;
      return { entities: byEntity.size, before, after, delta, up, down, pct: before !== 0 ? (delta / before) * 100 : null };
    };
    budget.totals = totalsOf(budget.items);
    // Platform bazında kırılım (Meta'da ne kadar arttı, Google'da ne kadar düştü).
    budget.byPlatform = ['GOOGLE_ADS', 'META_ADS']
      .map((p) => ({ platform: p, ...totalsOf(budget.items.filter((it) => it.platform === p)) }))
      .filter((t) => t.entities > 0);
    budget.items.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    return { budget, status, created, removed, noise, other, meaningful: rows.length - noise };
  }, [rows]);

  const exportExcel = () => {
    if (!rows.length) { toast.error('No records to export'); return; }
    const wb = XLSX.utils.book_new();
    const detail = rows.map(c => ({
      Date: fmtDateTime(c.datetime),
      Platform: c.platform === 'GOOGLE_ADS' ? 'Google' : 'Meta',
      Action: actionLabel(c.action),
      Entity: c.entity_name || '',
      'Entity Type': c.entity_type || '',
      Field: c.field || '',
      'Old Value': c.old_value || '',
      'New Value': c.new_value || '',
      Actor: c.actor || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), 'Changes');
    const sum = data?.summary || {};
    const sumRows = [
      { Metric: 'Total', Value: sum.total || 0 },
      { Metric: 'Meaningful changes', Value: digest.meaningful },
      { Metric: 'Budget changes', Value: digest.budget.count },
      { Metric: 'Total budget (before)', Value: Math.round(digest.budget.totals?.before || 0) },
      { Metric: 'Total budget (after)', Value: Math.round(digest.budget.totals?.after || 0) },
      { Metric: 'Net budget change (₺)', Value: Math.round(digest.budget.totals?.delta || 0) },
      { Metric: 'Net budget change (%)', Value: digest.budget.totals?.pct != null ? Number(digest.budget.totals.pct.toFixed(1)) : '' },
      { Metric: 'Paused', Value: digest.status.paused },
      { Metric: 'Activated', Value: digest.status.activated },
      { Metric: 'Created', Value: digest.created },
      { Metric: 'Removed', Value: digest.removed },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sumRows), 'Summary');
    const slug = (data?.brandName || 'brand').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    XLSX.writeFile(wb, `change-history-${slug}-${source}-${days}d.xlsx`);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <AlertTriangle className="w-12 h-12 text-gray-300" />
        <p className="text-gray-600 text-sm">{error}</p>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700">
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  const summary = data?.summary || {};
  const conns = data?.connections || {};
  const actionOpts = sortedEntries(summary.byAction).map(([k]) => k);
  const entityOpts = sortedEntries(summary.byEntityType).map(([k]) => k);
  const actorOpts = sortedEntries(summary.byActor).map(([k]) => k);
  const b = digest.budget;

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Ad Change History
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {data?.brandName || selectedBrand?.name} ·{' '}
              {source === 'archive' ? 'Archive (persistent)' : 'Live (API)'} ·{' '}
              {conns.google ? 'Google ✓' : 'Google —'} {conns.meta ? 'Meta ✓' : 'Meta —'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-white rounded-xl border border-gray-200 p-0.5">
              <button onClick={() => switchSource('archive')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${source === 'archive' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Database className="w-3.5 h-3.5" /> Archive
              </button>
              <button onClick={() => switchSource('live')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${source === 'live' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Zap className="w-3.5 h-3.5" /> Live
              </button>
            </div>
            <div className="flex bg-white rounded-xl border border-gray-200 p-0.5">
              {dayOptions.map(o => (
                <button key={o.value} onClick={() => setDays(o.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${days === o.value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <button onClick={exportExcel} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-xs font-medium rounded-xl hover:bg-emerald-700" title="Export to Excel">
              <Download className="w-4 h-4" /> Excel
            </button>
            <button onClick={fetchData} className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50" title="Refresh">
              <RefreshCw className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Calendar + Digest side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <MonthCalendar dayMap={dayMap} selectedDay={selectedDay} onSelectDay={setSelectedDay} initialDay={latestDay} />

          {/* Digest / özet */}
          <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-4 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-400">
                Summary {selectedDay ? `· ${fmtDateTime(selectedDay + 'T00:00:00').slice(0, 8)}` : (days === 0 ? '· today' : `· last ${days} days`)}
              </p>
              <span className="text-[11px] text-gray-400">
                {digest.meaningful} meaningful{digest.noise ? ` · ${digest.noise} system events hidden` : ''}
              </span>
            </div>

            {/* Budget highlight */}
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-800"><Wallet className="w-4 h-4" /> Budget / Bid</span>
                <span className="text-xs text-gray-600">
                  {b.count} changes
                  {b.count > 0 && <> · <span className="text-emerald-600 font-medium">{b.up}↑</span> <span className="text-rose-600 font-medium">{b.down}↓</span></>}
                </span>
              </div>

              {/* Toplam net bütçe etkisi (öğe başına ilk→son bütçeden hesaplanır) */}
              {b.totals && b.totals.entities > 0 && b.totals.before > 0 && (
                <div className="rounded-lg bg-white/70 border border-amber-200 px-3 py-2 mb-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-gray-500">
                      Total budget <span className="text-gray-400">({b.totals.entities} items)</span>
                    </span>
                    <span className="whitespace-nowrap text-xs">
                      <span className="text-gray-500">{fmtAmt(b.totals.before)}</span>
                      <span className="mx-1 text-gray-300">→</span>
                      <span className="font-semibold text-gray-900">{fmtAmt(b.totals.after)}</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-[11px] text-gray-500">Net change</span>
                    <span className={`flex items-center gap-1 text-sm font-bold ${b.totals.delta > 0 ? 'text-emerald-600' : b.totals.delta < 0 ? 'text-rose-600' : 'text-gray-500'}`}>
                      {b.totals.delta > 0 ? <ArrowUp className="w-3.5 h-3.5" /> : b.totals.delta < 0 ? <ArrowDown className="w-3.5 h-3.5" /> : null}
                      {b.totals.delta > 0 ? '+' : ''}{fmtAmt(b.totals.delta).replace('₺', '')}₺
                      <span className="ml-1">({fmtPct(b.totals.pct)})</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Platform bazında kırılım: Meta'da ne kadar arttı, Google'da ne kadar düştü */}
              {b.byPlatform && b.byPlatform.length > 0 && (
                <div className={`grid ${b.byPlatform.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-2 mb-2`}>
                  {b.byPlatform.map((t) => (
                    <div key={t.platform} className="rounded-lg bg-white/70 border border-amber-200 px-2.5 py-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-gray-700">
                          {t.platform === 'GOOGLE_ADS' ? 'Google Ads' : 'Meta'}
                          <span className="ml-1 font-normal text-gray-400">({t.entities})</span>
                        </span>
                        <span className="text-[10px] text-gray-400">
                          <span className="text-emerald-600">{t.up}↑</span> <span className="text-rose-600">{t.down}↓</span>
                        </span>
                      </div>
                      <div className={`mt-0.5 text-sm font-bold flex items-center gap-1 ${t.delta > 0 ? 'text-emerald-600' : t.delta < 0 ? 'text-rose-600' : 'text-gray-500'}`}>
                        {t.delta > 0 ? <ArrowUp className="w-3.5 h-3.5" /> : t.delta < 0 ? <ArrowDown className="w-3.5 h-3.5" /> : null}
                        {t.delta > 0 ? '+' : ''}{fmtAmt(t.delta).replace('₺', '')}₺
                        <span className="font-normal text-[10px] text-gray-400">({fmtPct(t.pct)})</span>
                      </div>
                      <div className="text-[10px] text-gray-400">{fmtAmt(t.before)} → {fmtAmt(t.after)}</div>
                    </div>
                  ))}
                </div>
              )}

              {b.items.length === 0 ? (
                <p className="text-xs text-gray-400 py-1">No quantifiable budget changes in this period.</p>
              ) : (
                <div className="space-y-0.5">
                  {b.items.slice(0, 4).map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs py-1 border-t border-amber-100 first:border-0">
                      <span className="truncate text-gray-700 flex items-center gap-1.5" title={it.name}>
                        {it.delta > 0 ? <ArrowUp className="w-3 h-3 text-emerald-600 shrink-0" /> : <ArrowDown className="w-3 h-3 text-rose-600 shrink-0" />}
                        {it.name}
                      </span>
                      <span className="whitespace-nowrap shrink-0">
                        <span className="text-gray-400">{fmtAmt(it.from)}</span>
                        <span className="mx-1 text-gray-300">→</span>
                        <span className="font-medium text-gray-900">{fmtAmt(it.to)}</span>
                        <span className={`ml-1.5 font-semibold ${it.delta > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(it.pct)}</span>
                      </span>
                    </div>
                  ))}
                  {b.items.length > 4 && <p className="text-[11px] text-gray-400 pt-1">+{b.items.length - 4} more budget changes</p>}
                </div>
              )}
            </div>

            {/* Status / create / remove chips */}
            <div className="grid grid-cols-3 gap-2 mt-auto">
              <div className="rounded-xl border border-gray-200 p-2.5 text-center">
                <Power className="w-4 h-4 text-blue-500 mx-auto mb-1" />
                <p className="text-sm font-bold text-gray-900">{digest.status.paused}<span className="text-gray-300">/</span>{digest.status.activated}</p>
                <p className="text-[10px] text-gray-400">paused / active</p>
              </div>
              <div className="rounded-xl border border-gray-200 p-2.5 text-center">
                <PlusCircle className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
                <p className="text-sm font-bold text-gray-900">{digest.created}</p>
                <p className="text-[10px] text-gray-400">created</p>
              </div>
              <div className="rounded-xl border border-gray-200 p-2.5 text-center">
                <Trash2 className="w-4 h-4 text-rose-500 mx-auto mb-1" />
                <p className="text-sm font-bold text-gray-900">{digest.removed}</p>
                <p className="text-[10px] text-gray-400">removed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <StatCard title="Total Actions">
            <p className="text-3xl font-bold text-gray-900">{summary.total || 0}</p>
            <div className="flex gap-2 mt-2">
              <span className="text-xs text-blue-700">Google {summary.byPlatform?.GOOGLE_ADS || 0}</span>
              <span className="text-xs text-indigo-700">Meta {summary.byPlatform?.META_ADS || 0}</span>
            </div>
          </StatCard>
          <StatCard title="Action Types">
            <div className="space-y-1">
              {sortedEntries(summary.byAction).slice(0, 4).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 truncate pr-2">{actionLabel(k)}</span>
                  <span className="font-semibold text-gray-900">{v}</span>
                </div>
              ))}
            </div>
          </StatCard>
          <StatCard title="Who Did It">
            <div className="space-y-1">
              {sortedEntries(summary.byActor).slice(0, 4).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 truncate pr-2 flex items-center gap-1"><User className="w-3 h-3 text-gray-400" />{k}</span>
                  <span className="font-semibold text-gray-900">{v}</span>
                </div>
              ))}
            </div>
          </StatCard>
          <StatCard title="Entity Types">
            <div className="space-y-1">
              {sortedEntries(summary.byEntityType).slice(0, 4).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 truncate pr-2">{k}</span>
                  <span className="font-semibold text-gray-900">{v}</span>
                </div>
              ))}
            </div>
          </StatCard>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {selectedDay && (
            <button onClick={() => setSelectedDay(null)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl bg-blue-600 text-white">
              📅 {fmtDateTime(selectedDay + 'T00:00:00').slice(0, 8)} ✕
            </button>
          )}
          <div className="flex bg-white rounded-xl border border-gray-200 p-0.5">
            {['ALL', 'GOOGLE_ADS', 'META_ADS'].map(p => (
              <button key={p} onClick={() => setPlatformFilter(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${platformFilter === p ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {p === 'ALL' ? 'All' : p === 'GOOGLE_ADS' ? 'Google' : 'Meta'}
              </button>
            ))}
          </div>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 max-w-[180px]">
            <option value="ALL">All actions</option>
            {actionOpts.map(o => <option key={o} value={o}>{actionLabel(o)}</option>)}
          </select>
          <select value={entityFilter} onChange={e => setEntityFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 max-w-[160px]">
            <option value="ALL">All entity types</option>
            {entityOpts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={actorFilter} onChange={e => setActorFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 max-w-[160px]">
            <option value="ALL">Everyone</option>
            {actorOpts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <button onClick={() => setBudgetOnly(v => !v)}
            className={`px-3 py-2 text-xs font-medium rounded-xl border transition ${budgetOnly ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            Budget/bid only
          </button>
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search campaign, person, field…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </div>
          <span className="text-xs text-gray-400 ml-auto">{rows.length} records</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Platform</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                  <th className="px-4 py-3 font-semibold">Entity</th>
                  <th className="px-4 py-3 font-semibold">Change</th>
                  <th className="px-4 py-3 font-semibold">Who</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-sm">
                    {source === 'archive' ? 'No records in the archive for this period. (Archive accumulates daily at 05:40.)' : 'No records in this period.'}
                  </td></tr>
                )}
                {rows.map((c, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/60">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{fmtDateTime(c.datetime)}</td>
                    <td className="px-4 py-3"><PlatformBadge platform={c.platform} /></td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium border ${actionTone(c.action)}`}>{actionLabel(c.action)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900 font-medium">{c.entity_name || '—'}</span>
                      <span className="block text-[11px] text-gray-400">{c.entity_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      {c.field && <span className="block text-[11px] text-gray-400">{c.field}</span>}
                      {(c.old_value || c.new_value) ? (
                        <span className="text-xs">
                          <span className="text-rose-600 line-through">{c.old_value || '—'}</span>
                          <span className="mx-1 text-gray-400">→</span>
                          <span className="text-emerald-700 font-medium">{c.new_value || '—'}</span>
                        </span>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{c.actor || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
